import { deepClone } from '../redux/store';
import { IInstallationSettings, ISettingsResponse } from './installation';
import { IAccount, IXeroTaxRate } from './xero';

export enum AccountSettingType {
    Sales,
    Discounts,
    Shipping,
    Refunds,
    Fees,
    Tax,
    GiftCards,
}

export enum AccountSettingErrorType {
    NoError,
    AccountDeleted,
    AccountInvalid,
}

export interface IAccountSettings {
    accountSettingType: AccountSettingType;
    defaultToSuggestion: boolean;
    eligibleAccounts: string[];
    errorType: AccountSettingErrorType;
    selectedAccountId?: string;
    suggestedAccount: IAccount;
}

export interface IPaymentGateway {
    code: string;
    name: string;
    used: boolean;
    reconcilable: boolean;
    isPopular: boolean;
}

export interface IShopifySettingsResponse extends ISettingsResponse {
    shop: string;
    shopDetails: IShopifyShopDetails | null;
    paymentGatewayDetails: Record<string, IPaymentGatewayDetail> | null;
    paymentGateways: IPaymentGateway[];
    paysTax: boolean;
    bankAccounts: IAccount[];
    recommendedClearingAccountName: string;
    chartOfAccounts: IAccount[];
    xeroTaxRates: IXeroTaxRate[];
    chartOfAccountsSettings: IAccountSettings[];
    contactId: string | null;
    syncFromDateUtc: string | null;
    syncToDateUtc: string | null;
    xeroRevenueTaxRates: IXeroTaxRate[] | null;
    xeroRegion: string;
    taxAuthorities: ITaxAuthority[] | null;
    accountAndTaxMappings: IAccountAndTaxMapping[];
    shopifyTaxRegions: IShopifyTaxRegion[] | null;
    supportsTaxCollection: boolean | false;
}

export interface IShopifySettingsPutRequest extends IInstallationSettings {
    shop: string;
    paymentGatewayDetails: Record<string, IPaymentGatewayDetail> | null;
    salesAccountId: string | null;
    discountsAccountId: string | null;
    shippingAccountId: string | null;
    refundsAccountId: string | null;
    feesAccountId: string | null;
    taxCollectedAccountId: string | null;
    giftCardsAccountId: string | null;
    syncFromDateUtc: string | null;
    syncToDateUtc: string | null;
    taxAuthorities: ITaxAuthority[] | null;
    accountAndTaxMappings: IAccountAndTaxMapping[];
}

export interface IShopifyShopDetails {
    currencyCode: string | null;
}

export interface IPaymentGatewayDetail {
    bankAccountId?: string | null;
    bankAccountCurrency?: string | null;
    clearingAccountId?: string | null;
    clearingAccountCurrency?: string | null;
    clearingAccountName?: string | null;
    clearingAccountNumber?: string | null;
    createInvoice: boolean | null;
}

export interface ITablePaymentGatewayDetail extends IPaymentGatewayDetail {
    paymentType: string;
}

export interface IAccountAndTaxMapping {
    region: string;
    transactionType: string;
    shopifyTaxRate?: IShopifyTaxRate;
    xeroAccountId?: string;
    xeroAccountIdOnLoad?: string;
    xeroTaxType?: string;
    recommendedAccount?: IAccount;
    useRecommendedAccount: boolean;
    xeroTaxTypeOnLoad?: string;
}

export interface IShopifyTaxRate {
    countryCode: string;
    ratePercentage: number;
    title: string;
}

export interface IShopifyTaxRegion {
    code: string;
    name: string;
}

export interface ITaxAuthority {
    xeroTaxAuthority: string | null;
    xeroTaxRate?: number | null;
    xeroTaxType?: string | null;
    shopifyTaxRegionCode?: string | null;
}

/**
 * Ensures that all payment gateway details have a currency code associated
 * @param details raw payment gateway details received from api
 * @param bankAccounts Bank accounts which are the source of truth for accounts and contain all data
 * @returns Sanitised payment gateway details record with currency codes guaranteed to be filled in
 */
function paymentGatewayDetailSanitiser(
    details: Record<string, IPaymentGatewayDetail> | null,
    bankAccounts: IAccount[]
): Record<string, IPaymentGatewayDetail> | null {
    if (details) {
        return Object.entries(details)
            .map(([key, value]): [string, IPaymentGatewayDetail] => {
                const newValue = { ...value };
                if (!value.bankAccountCurrency) {
                    const correspondingBA = bankAccounts.find((f) => f.accountId === value.bankAccountId);
                    newValue.bankAccountCurrency = correspondingBA?.currencyCode;
                }
                if (!value.clearingAccountCurrency) {
                    const correspondingCA = bankAccounts.find((f) => f.accountId === value.clearingAccountId);
                    newValue.clearingAccountCurrency = correspondingCA?.currencyCode;
                }
                return [key, newValue];
            })
            .reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {} as Record<string, IPaymentGatewayDetail>);
    }
    return null;
}

export function convertShopifySettings(settings: IShopifySettingsResponse): IShopifySettingsPutRequest {
    const getSelectedAccountId = (type: AccountSettingType) =>
        settings.chartOfAccountsSettings?.find((f) => f.accountSettingType === type)?.selectedAccountId ?? null;

    return {
        discountsAccountId: getSelectedAccountId(AccountSettingType.Discounts),
        feesAccountId: getSelectedAccountId(AccountSettingType.Fees),
        giftCardsAccountId: getSelectedAccountId(AccountSettingType.GiftCards),
        installationId: settings.installationId,
        paymentGatewayDetails: paymentGatewayDetailSanitiser(settings.paymentGatewayDetails, settings.bankAccounts),
        refundsAccountId: getSelectedAccountId(AccountSettingType.Refunds),
        salesAccountId: getSelectedAccountId(AccountSettingType.Sales),
        shippingAccountId: getSelectedAccountId(AccountSettingType.Shipping),
        shop: settings.shop,
        taxCollectedAccountId: getSelectedAccountId(AccountSettingType.Tax),
        taxAuthorities: deepClone(settings.taxAuthorities),
        syncToDateUtc: settings.syncToDateUtc,
        syncFromDateUtc: settings.syncFromDateUtc,
        accountAndTaxMappings: deepClone(settings.accountAndTaxMappings),
    };
}
